/**
 * Pure, role-aware permission helpers for workspace UI controls.
 *
 * The server is the source of truth — backend routers re-check every role
 * via the `requires_role()` dependency in `app/auth/workspace_acl.py`. These
 * helpers exist to drive UI affordances (hide an invite button when the
 * viewer can't invite, gray out a "delete project" when the editor isn't
 * an admin) WITHOUT shipping a second copy of the authorization rules.
 *
 * Rules of thumb when adding a new permission:
 *  1. Define the minimum role on the backend first (where it's enforced).
 *  2. Mirror it here so the UI matches.
 *  3. Don't branch on `is_personal` for permissions — a personal workspace
 *     is owned by exactly one user with role `owner`, so the role check
 *     already implies it.
 */

import type { WorkspaceRole } from './queries'

/** Strict total order over roles — higher number = more privilege. */
const ROLE_RANK: Record<WorkspaceRole, number> = {
  viewer: 0,
  editor: 1,
  admin: 2,
  owner: 3,
}

/**
 * True when `role` is at least as privileged as `minimum`. Used by feature
 * helpers below; exposed for one-off comparisons in product code that don't
 * map cleanly to one of the named helpers.
 */
export function roleAtLeast(
  role: WorkspaceRole | null | undefined,
  minimum: WorkspaceRole,
): boolean {
  if (!role) return false
  return ROLE_RANK[role] >= ROLE_RANK[minimum]
}

/** Can rename / archive the workspace itself. owner+admin only. */
export function canManageWorkspace(role: WorkspaceRole | null | undefined) {
  return roleAtLeast(role, 'admin')
}

/** Can invite new members or revoke invites. owner+admin only. */
export function canManageMembers(role: WorkspaceRole | null | undefined) {
  return roleAtLeast(role, 'admin')
}

/** Can create/rename/delete projects inside the workspace. editor+. */
export function canManageProjects(role: WorkspaceRole | null | undefined) {
  return roleAtLeast(role, 'editor')
}

/**
 * Can view audit logs (sensitive — same gate as member management).
 * owner+admin only.
 */
export function canViewAudit(role: WorkspaceRole | null | undefined) {
  return roleAtLeast(role, 'admin')
}

/**
 * Only the owner of a workspace can transfer ownership or delete it
 * outright. The backend rejects everything below `owner`.
 */
export function canTransferOwnership(role: WorkspaceRole | null | undefined) {
  return roleAtLeast(role, 'owner')
}

/** Friendly label used in role pickers and badges. Keep in sync with backend enum. */
export const ROLE_LABELS: Record<WorkspaceRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  editor: 'Editor',
  viewer: 'Viewer',
}

/** Roles assignable via the invite flow. `owner` cannot be invited — it is
 *  transferred from an existing owner. */
export const INVITABLE_ROLES: readonly WorkspaceRole[] = [
  'admin',
  'editor',
  'viewer',
] as const
